<script setup lang="ts">
  import { ref, onMounted, computed, reactive, watch } from 'vue'
  import {
    CaseCreationIntegrationService,
    CasesService,
    FormsService
  } from '@/types/generated/services.gen'
  import type {
    FileDataRequest,
    IntegrationSpecificDataRequest,
    IntegrationTypeEnum,
    SchemaExternalIntegrationCaseCreationRequest,
    CustomerCase,
    PatchedCustomerCaseRequest,
    ExternalIntegrationFileDownloadResponse
  } from '@/types/generated/types.gen'
  import router from '@/router'
  import ActaportForm from '@/components/integrations/actaport.vue'
  import KleosForm from '@/components/integrations/kleos.vue'
  import PersonalInformation from '@/components/integrations/personalInformation.vue'
  import OpponentInformation from '@/components/integrations/opponentInformation.vue'
  import LegalInsurance from '@/components/integrations/legalInsuranceSection.vue'
  import SelectDocumentSection from '@/components/integrations/selectDocumentSection.vue'
  import { ArrowLeftIcon } from '@heroicons/vue/24/solid'
  import AdvowareForm from '@/components/integrations/advoware.vue'
  import JurnodesForm from '@/components/integrations/jurnodes.vue'
  import AdvoluxForm from '@/components/integrations/advolux.vue'
  import LawFirmSoftwareForm from '@/components/integrations/lawfirm.vue'
  import RaMicroForm from '@/components/integrations/ramicro.vue'
  import RaMicroApiForm from '@/components/integrations/ramicroapi.vue'
  import LoadingComponent from '@/components/integrations/loadingPage.vue'
  import { useRouter } from 'vue-router'
  import Entities from '@/components/app/cases/entities/Entities.vue'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'

  const host = import.meta.env.VITE_HOST
  const caseData = ref<CustomerCase | null>(null)
  const isLoading = ref(true)
  const isEntitiesLoading = ref(true)
  const isFormsLoading = ref(true)
  const error = ref<string | null>(null)
  const showErrorNotification = ref(false)
  const documentSizeValid = ref(true)

  const apiPayload = ref<SchemaExternalIntegrationCaseCreationRequest>({
    integrationType: 'actaport', // Default value, will be updated
    mandateId: 0, // Will be updated when case data is loaded
    integrationSpecificData: {
      export_opponent_information: false,
      use_legal_insurance: false,
      entities_with_relationships: []
    } as IntegrationSpecificDataRequest
  })

  const showSuccessModal = ref(false)
  const handleModalClose = () => {
    showSuccessModal.value = false
    // Redirect to the case page
    const path = `cases/${caseData.value?.id}/`
    const baseUrl = `${window.location.protocol}//${window.location.host}`
    const absoluteUrl = new URL(path, baseUrl).href
    window.location.href = absoluteUrl
  }

  const integrationMode = computed(() => {
    if (!caseData.value) return null

    const modes = [
      {
        name: 'Actaport',
        enabled: caseData.value.org.is_actaport_export_integration_enabled,
        id: null
      },
      {
        name: 'Advolux',
        enabled: caseData.value.org.is_advolux_export_integration_enabled,
        id: null
      },
      {
        name: 'Advoware',
        enabled: caseData.value.org.is_advoware_export_integration_enabled,
        id: null
      },
      {
        name: 'LawFirm',
        enabled: caseData.value.org.is_law_firm_software_exportation_enabled,
        id: null
      },
      {
        name: 'RaMicro',
        enabled: caseData.value.org.is_ra_micro_export_integration_enabled,
        id: null
      },
      {
        name: 'RaMicroApi',
        enabled: caseData.value.org.is_ra_micro_export_integration_enabled,
        id: null
      },
      {
        name: 'Jurnodes',
        enabled: caseData.value.org.is_jurnodes_export_integration_enabled,
        id: null
      },
      {
        name: 'Kleos',
        enabled: caseData.value.org.is_kleos_integration_enabled,
        id: null
      },
      {
        name: 'Annotext',
        enabled: caseData.value.org.is_annotext_export_integration_enabled,
        id: null
      }
    ]

    // Check the route's integration_name parameter
    const routeIntegration = router.currentRoute.value.params.integration_name
      ?.toString()
      .toLowerCase()

    if (routeIntegration) {
      const matchedMode = modes.find(
        (mode) => mode.name.toLowerCase() === routeIntegration && mode.enabled
      )
      if (matchedMode) return matchedMode.name
    }

    // If no match found or no route integration specified, fall back to previous logic
    const enabledMode = modes.find((mode) => mode.enabled)
    return enabledMode ? enabledMode.name : null
  })

  const downloadableContent = computed(() => {
    return ['Advolux', 'LawFirm', 'RaMicro', 'Annotext'].includes(integrationMode.value as string)
  })

  const fetchCaseData = async () => {
    try {
      const fetchedCaseData = (await CasesService.casesRetrieve({
        uuid: router.currentRoute.value.params.uuid.toString()
      })) as CustomerCase

      caseData.value = fetchedCaseData

      if (caseData.value) {
        apiPayload.value.mandateId = caseData.value.id
        apiPayload.value.integrationType = (integrationMode.value?.toLowerCase() ||
          '') as IntegrationTypeEnum
      }
    } catch (e) {
      error.value = 'Failed to load case data. Please try again.'
      console.error('Error loading case data:', e)
      throw e
    } finally {
      isLoading.value = false
    }
  }

  const caseStore = useCaseStore()

  onMounted(async () => {
    try {
      isLoading.value = true
      isEntitiesLoading.value = true
      isFormsLoading.value = true

      await fetchCaseData()

      // Load forms and entities in parallel
      await Promise.all([
        FormsService.formsList().then(() => {
          isFormsLoading.value = false
        }),
        caseStore.loadCase(router.currentRoute.value.params.uuid as string).then(() => {
          isEntitiesLoading.value = false
        })
      ])
    } catch (e) {
      error.value = 'Failed to load data. Please try again.'
      console.error('Error loading data:', e)
    }
  })

  const handleDataUpdate = async () => {
    await fetchCaseData()
  }

  const isOpponentExported = ref(false)
  const exportLegalInsuranceData = ref(false)

  const handleUpdatePayload = (updatedData: CustomerCase) => {
    if (updatedData) {
      apiPayload.value.integrationSpecificData = {
        ...apiPayload.value.integrationSpecificData,
        ...updatedData
      }
    }
  }

  const handleExportOpponentData = (exportOpponentData: boolean) => {
    isOpponentExported.value = exportOpponentData
    apiPayload.value.integrationSpecificData.export_opponent_information = exportOpponentData
  }

  const handleExportLegalInsuranceData = (val: boolean) => {
    exportLegalInsuranceData.value = val
    apiPayload.value.integrationSpecificData.use_legal_insurance = val
  }

  const selectedEntitiesRelationships = ref<Array<string>>([])

  // Map entity UUIDs to entity types for title generation
  const mappedSelectedEntities = computed(() => {
    if (!caseData.value?.data.entities) {
      return ['client'] // Client is always included by default
    }

    const entityTypes: string[] = ['client'] // Client is always included by default

    // Find all opponent entities - check for both ENTITIES_OPPONENT and entities.opponent
    const opponentEntities = caseData.value.data.entities.filter((entity) =>
      entity.relationships.some(
        (rel) =>
          rel.relationship_type === 'ENTITIES_OPPONENT' ||
          rel.relationship_type === 'entities.opponent'
      )
    )

    // Check if any opponent entity is selected
    const isOpponentSelected = selectedEntitiesRelationships.value.some((uuid) =>
      opponentEntities.some((entity) => entity.uuid === uuid)
    )

    if (isOpponentSelected) {
      entityTypes.push('opponent')
    }

    return entityTypes
  })

  const handleSelectEntitiesWithRelationships = (val: Array<string>) => {
    // selectedEntities.value = val
    apiPayload.value.integrationSpecificData = {
      ...apiPayload.value.integrationSpecificData,
      entities_with_relationships: val
    }

    // Update the selected entity relationships
    selectedEntitiesRelationships.value = val
  }

  const handleFileDataUpdate = (newFileData: FileDataRequest[]) => {
    apiPayload.value.integrationSpecificData = {
      ...apiPayload.value.integrationSpecificData,
      fileData: newFileData
    }
  }

  const handleSaveInfo = async (infoType: 'client' | 'opponent', updatedData: any) => {
    if (caseData.value) {
      try {
        // Create a copy of the updatedData
        const processedData = { ...updatedData }

        // Check if date_of_birth is present
        if ('date_of_birth' in processedData) {
          // Convert empty string to null for date_of_birth
          processedData.date_of_birth =
            processedData.date_of_birth === '' ? null : processedData.date_of_birth
        }

        const response = await CasesService.casesPartialUpdate({
          uuid: caseData.value.uuid as string,
          requestBody: {
            data: {
              [infoType]: {
                ...caseData.value.data[infoType],
                ...processedData
              }
            }
          } as PatchedCustomerCaseRequest
        })

        // Update local caseData
        caseData.value.data[infoType] = {
          ...caseData.value.data[infoType],
          ...processedData
        }
      } catch (e) {
        console.error(`Error updating ${infoType} information:`, e)
      }
    }
  }

  const personalInformationRef = ref<InstanceType<typeof PersonalInformation> | null>(null)
  const opponentInformationRef = ref<InstanceType<typeof OpponentInformation> | null>(null)
  const actaportFormRef = ref<InstanceType<typeof ActaportForm> | null>(null)
  const advowareFormRef = ref<InstanceType<typeof AdvowareForm> | null>(null)
  const kleosFormRef = ref<InstanceType<typeof KleosForm> | null>(null)
  const jurnodesFormRef = ref<InstanceType<typeof JurnodesForm> | null>(null)
  const advoluxFormRef = ref<InstanceType<typeof AdvoluxForm> | null>(null)
  const lawFirmSoftwareFormRef = ref<InstanceType<typeof LawFirmSoftwareForm> | null>(null)
  const raMicroFormRef = ref<InstanceType<typeof RaMicroForm> | null>(null)
  const raMicroApiFormRef = ref<InstanceType<typeof RaMicroApiForm> | null>(null)
  const legalInsuranceRef = ref<InstanceType<typeof LegalInsurance> | null>(null)

  const validationErrors = ref({})

  // Reactive validation for entities
  const entityValidationErrors = computed(() => {
    const errors = {}

    if (selectedEntitiesRelationships.value.length > 0 && caseStore.caseRecord?.data.entities) {
      const selectedEntities = caseStore.caseRecord.data.entities.filter((entity) =>
        selectedEntitiesRelationships.value.includes(entity.uuid)
      )

      for (const entity of selectedEntities) {
        // Validate date of birth
        const dateOfBirthStr = entity.entity.date_of_birth
        console.log('dateOfBirthStr =', dateOfBirthStr)
        if (dateOfBirthStr) {
          try {
            const [day, month, year] = dateOfBirthStr.split('.')
            const dateOfBirth = new Date(Number(year), Number(month) - 1, Number(day))

            // Logical date validity check
            if (
              dateOfBirth.getFullYear() !== Number(year) ||
              dateOfBirth.getMonth() !== Number(month) - 1 ||
              dateOfBirth.getDate() !== Number(day)
            ) {
              errors[`entity_${entity.uuid}`] = {
                type: null,
                message: `Bitte geben Sie ein existierendes Datum im Format TT.MM.JJJJ für ${entity.entity.title} ${entity.entity.first_name} ${entity.entity.last_name} ein.`
              }
              continue // Skip further checks for this entity
            }

            const now = new Date()
            // Normalize both dates to ignore the time part
            const dateOfBirthDate = new Date(
              dateOfBirth.getFullYear(),
              dateOfBirth.getMonth(),
              dateOfBirth.getDate()
            )
            const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
            if (dateOfBirthDate >= nowDate) {
              let errorMessage = `Das Geburtsdatum von ${entity.entity.title} ${entity.entity.first_name} ${entity.entity.last_name} muss in der Vergangenheit liegen.`
              if (!entity.entity.first_name && !entity.entity.last_name) {
                if (entity.entity.entity_type === 'legal_person') {
                  errorMessage = `Das Geburtsdatum von den Contact Info von ${entity.entity.company_name} muss in der Vergangenheit liegen.`
                } else {
                  // institution
                  errorMessage = `Das Geburtsdatum von den Contact Info von ${entity.entity.institution_name} muss in der Vergangenheit liegen.`
                }
              }
              errors[`entity_${entity.uuid}`] = {
                type: null,
                message: errorMessage
              }
            }
          } catch (error) {
            errors[`entity_${entity.uuid}`] = {
              type: null,
              message: `Bitte geben Sie ein gültiges Datum im Format TT.MM.JJJJ für ${entity.entity.title} ${entity.entity.first_name} ${entity.entity.last_name} ein.`
            }
          }
        }
        if (entity.entity.entity_type === 'natural_person') {
          // Validate natural person has last name
          const lastName = entity.entity.last_name

          if (!lastName) {
            errors[`entity_${entity.uuid}`] = {
              type: 'natural_person',
              message: 'Der Nachname ist erforderlich.'
            }
          }
        } else if (entity.entity.entity_type === 'legal_person') {
          // Validate legal person has company name
          const companyName = entity.entity.company_name
          if (!companyName) {
            errors[`entity_${entity.uuid}`] = {
              type: 'legal_person',
              message: 'Der Firmenname ist erforderlich.'
            }
          }
        } else if (entity.entity.entity_type === 'institution') {
          // Validate institution has institution name
          const institutionName = entity.entity.institution_name
          if (!institutionName) {
            errors[`entity_${entity.uuid}`] = {
              type: 'institution',
              message: 'Der Name der Institution ist erforderlich.'
            }
          }
        }
      }
    }

    return errors
  })

  // Watch for changes in entity validation errors
  watch(entityValidationErrors, (newErrors) => {
    if (Object.keys(newErrors).length > 0) {
      validationErrors.value = {
        ...validationErrors.value,
        entities: newErrors
      }
    } else if (validationErrors.value.entities) {
      // Remove entity errors if they're now valid
      const { entities, ...rest } = validationErrors.value
      validationErrors.value = rest
    }
  })

  // Watch for changes in selected entities
  watch(
    selectedEntitiesRelationships,
    () => {
      // This will trigger the entityValidationErrors computed property
      // which will update the validation errors
    },
    { deep: true }
  )

  const validateAllForms = async () => {
    const formRefs = [
      { name: 'personalInformation', ref: personalInformationRef },
      { name: 'opponentInformation', ref: opponentInformationRef },
      { name: 'actaportForm', ref: actaportFormRef },
      { name: 'advoluxForm', ref: advoluxFormRef },
      { name: 'advowareForm', ref: advowareFormRef },
      { name: 'kleosForm', ref: kleosFormRef },
      { name: 'jurnodesForm', ref: jurnodesFormRef },
      { name: 'lawFirmSoftwareForm', ref: lawFirmSoftwareFormRef },
      { name: 'raMicroForm', ref: raMicroFormRef },
      { name: 'raMicroApiForm', ref: raMicroApiFormRef },
      { name: 'legalInsurance', ref: legalInsuranceRef }
    ]

    // Check document size validation for RaMicroAPI
    if (integrationMode.value === 'RaMicroApi' && !documentSizeValid.value) {
      validationErrors.value['documents'] = {
        size: 'Die Gesamtgröße der ausgewählten Dokumente überschreitet das Limit von 10MB.'
      }
    }

    // Use the reactive computed property for entity validation
    const entitiesValid = Object.keys(entityValidationErrors.value).length === 0

    const validationResults = await Promise.all(
      formRefs.map(async ({ name, ref }) => {
        if (ref.value && typeof ref.value.validate === 'function') {
          if (!isOpponentExported.value && name === 'opponentInformation') {
            return { formName: name, valid: true, errors: {} }
          }

          const result = await ref.value.validate()
          return { formName: name, ...result }
        }
        return { formName: name, valid: true, errors: {} }
      })
    )

    const allFormsValid = validationResults.every((result) => result.valid)
    const errors = validationResults.reduce((acc, result) => {
      if (!result.valid) {
        acc[result.formName] = result.errors
      }
      return acc
    }, {})

    // Add entity errors to validation errors
    if (!entitiesValid) {
      errors['entities'] = entityValidationErrors.value
    }

    validationErrors.value = errors

    return allFormsValid && entitiesValid
  }

  const showValidationErrors = () => {
    const formRefs = [
      { name: 'personalInformation', ref: personalInformationRef },
      { name: 'opponentInformation', ref: opponentInformationRef },
      { name: 'actaportForm', ref: actaportFormRef },
      { name: 'advoluxForm', ref: advoluxFormRef },
      { name: 'advowareForm', ref: advowareFormRef },
      { name: 'kleosForm', ref: kleosFormRef },
      { name: 'jurnodesForm', ref: jurnodesFormRef },
      { name: 'lawFirmSoftwareForm', ref: lawFirmSoftwareFormRef },
      { name: 'raMicroForm', ref: raMicroFormRef },
      { name: 'legalInsurance', ref: legalInsuranceRef }
    ]

    formRefs.forEach(({ name, ref }) => {
      if (validationErrors.value[name] && ref.value && typeof ref.value.showErrors === 'function') {
        ref.value.showErrors(validationErrors.value[name])
      }
    })

    // If there are entity errors, show a more specific error message
    if (validationErrors.value.entities) {
      showErrorNotification.value = true
      errorMessage.value =
        'Bitte füllen Sie die erforderlichen Felder für alle ausgewählten Beteiligten aus.'
      setTimeout(() => {
        showErrorNotification.value = false
      }, 5000)
    }
  }

  const vueRouter = useRouter()
  const showModal = ref(false)
  const modalStatus = ref<'loading' | 'success' | 'error'>('loading')

  // Computed property to check if all data is loaded
  const isAllDataLoaded = computed(() => {
    return !isLoading.value && !isEntitiesLoading.value && !isFormsLoading.value
  })

  // Check if form can be submitted (all data loaded and document size valid for RaMicroAPI)
  const canSubmitForm = computed(() => {
    if (integrationMode.value === 'RaMicroApi' && !documentSizeValid.value) {
      return false
    }
    return isAllDataLoaded.value
  })
  const errorMessage = ref<string>(
    'Es wurden nicht alle erforderlichen Felder für den Export ausgefüllt.'
  )

  const handleModalComplete = () => {
    showModal.value = false
    // Redirect to the case page
    const path = `cases/${caseData.value?.id}/`
    const baseUrl = `${window.location.protocol}//${window.location.host}`
    const absoluteUrl = new URL(path, baseUrl).href
    window.location.href = absoluteUrl
  }

  const handleRetry = () => {
    modalStatus.value = 'loading'
    handleSubmit()
  }

  const handleResetStatus = () => {
    modalStatus.value = 'loading'
    showModal.value = false
  }

  const handleSubmit = async () => {
    // Check document size validation for RaMicroAPI
    if (integrationMode.value === 'RaMicroApi' && !documentSizeValid.value) {
      showErrorNotification.value = true
      errorMessage.value =
        'Die Gesamtgröße der ausgewählten Dokumente überschreitet das Limit von 10MB.'
      setTimeout(() => {
        showErrorNotification.value = false
      }, 5000)
      return
    }

    const isValid = await validateAllForms()
    if (isValid) {
      showModal.value = true
      modalStatus.value = 'loading'
      try {
        const response = (await CaseCreationIntegrationService.caseCreationIntegrationCreate({
          requestBody: apiPayload.value
        })) as ExternalIntegrationFileDownloadResponse
        modalStatus.value = 'success'
        if (downloadableContent.value) {
          // Decode the base64 content
          const decodedContent = atob(response.content)
          // Convert the decoded content into a Uint8Array
          const byteArray = new Uint8Array(decodedContent.length)
          for (let i = 0; i < decodedContent.length; i++) {
            byteArray[i] = decodedContent.charCodeAt(i)
          }
          // Create a Blob from the byte array
          const blob = new Blob([byteArray], { type: response.contentType })
          // download the blob
          const blobUrl = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = blobUrl
          link.download = response.fileName
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(blobUrl)
        }
      } catch (error) {
        modalStatus.value = 'error'
      }
    } else {
      showValidationErrors()
      showErrorNotification.value = true
      setTimeout(() => {
        showErrorNotification.value = false
      }, 5000)
    }
  }

  const performActionText = computed(() => {
    if (downloadableContent.value) {
      return 'Akte herunterladen'
    }
    return 'Akte anlegen'
  })

  const actionIsBeingPerformedText = computed(() => {
    if (downloadableContent.value) {
      return 'Akte wird heruntergeladen...'
    }
    return 'Akte wird angelegt...'
  })

  const opponentEnabledList = [
    'Actaport',
    'Advoware',
    'Advolux',
    'Jurnodes',
    'RaMicro',
    'RaMicroApi',
    'Kleos',
    'LawFirm'
  ]

  const miscEntitiesEnabledList = [
    'Actaport',
    'Advoware',
    'Advolux',
    'Jurnodes',
    'RaMicro',
    'RaMicroApi',
    'Kleos',
    'LawFirm'
  ]
</script>

<template>
  <div class="flex min-h-screen flex-col">
    <div class="legacy-bg-color flex flex-grow items-start justify-center">
      <div
        v-if="showErrorNotification"
        class="fixed left-1/2 top-4 z-50 w-96 -translate-x-1/2 transform rounded border-l-4 border-red-500
          bg-red-100 p-4 text-red-700 shadow-md transition-all duration-500 ease-in-out"
        role="alert"
      >
        <div class="flex">
          <div class="py-1">
            <svg
              class="mr-4 h-6 w-6 fill-current text-red-500"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
            >
              <path
                d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z"
              />
            </svg>
          </div>
          <div>
            <p class="font-bold">Fehler</p>
            <p class="text-sm">
              {{ errorMessage }}
            </p>
            <ul
              v-if="validationErrors.entities"
              class="mt-2 text-sm"
            >
              <li
                v-for="(error, key) in validationErrors.entities"
                :key="key"
                class="ml-4 list-disc"
              >
                <span v-if="error.type === 'natural_person'">
                  Natürliche Person: {{ error.message }}
                </span>
                <span v-else-if="error.type === 'legal_person'">
                  Juristische Person: {{ error.message }}
                </span>
                <span v-else-if="error.type === 'institution'">
                  Institution: {{ error.message }}
                </span>
                <span v-else>{{ error.message }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="container px-16 py-6">
        <div class="flex w-full items-center justify-between pb-5">
          <h1
            v-if="integrationMode === 'RaMicroApi'"
            class="flex-1 whitespace-nowrap text-2xl font-semibold"
          >
            Anfrage an RA-MICRO exportieren
          </h1>
          <h1
            v-else
            class="flex-1 whitespace-nowrap text-2xl font-semibold"
          >
            Anfrage an {{ integrationMode }} exportieren
          </h1>
          <a
            v-if="caseData"
            :href="`/cases/${caseData.id}/`"
            class="inline-flex items-center gap-x-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm
              font-medium text-gray-800 shadow-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none
              disabled:pointer-events-none disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-800
              dark:text-white dark:hover:bg-neutral-700 dark:focus:bg-neutral-700"
          >
            <ArrowLeftIcon class="h-4 w-4" />
            Zurück zur Anfrage
          </a>
        </div>
        <div
          v-if="!showSuccess"
          class="flex flex-col gap-6"
        >
          <div
            v-if="isLoading || isEntitiesLoading || isFormsLoading"
            class="rounded-lg border border-gray-100 bg-white p-6 shadow-sm"
          >
            <div class="flex flex-col items-center justify-center py-4">
              <div class="loading-spinner">
                <div
                  v-for="n in 4"
                  :key="n"
                  class="spinner-quad"
                />
              </div>
              <p class="mt-6 text-base font-medium text-gray-900">Daten werden geladen...</p>
            </div>
          </div>

          <div
            v-if="error"
            class="rounded-xl bg-red-100 p-4 text-center"
          >
            <p class="text-red-700">{{ error }}</p>
          </div>

          <template v-else-if="!isLoading && !isEntitiesLoading && !isFormsLoading">
            <!-- Integration-specific sections -->
            <template v-if="integrationMode === 'Actaport'">
              <div
                class="rounded border-l-4 border-orange-500 bg-orange-100 p-4 text-orange-700 shadow-md transition-all
                  duration-500 ease-in-out"
                role="alert"
              >
                <div class="flex">
                  <div class="py-1">
                    <svg
                      class="mr-4 h-6 w-6 fill-current text-orange-500"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                    >
                      <path
                        d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p class="font-bold">Achtung</p>
                    <p class="text-sm">
                      Derzeit unterstützt diese Exportfunktion nicht die automatische Zuweisung von
                      Unterrollen zu Verfahrensbeteiligten beim Export nach Actaport. Bitte
                      überprüfen Sie die Unterrollen nach dem Export manuell in Actaport.
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <ActaportForm
                  v-if="caseData"
                  ref="actaportFormRef"
                  :case-data="caseData"
                  :debounce-delay="1000"
                  :throttle-interval="1000"
                  :selected-entities-relationships="selectedEntitiesRelationships"
                  :mapped-entity-types="mappedSelectedEntities"
                  @update:payload="handleUpdatePayload"
                />
              </div>
            </template>

            <template v-if="integrationMode === 'Advolux'">
              <div>
                <AdvoluxForm
                  v-if="caseData"
                  ref="advoluxFormRef"
                  :case-data="caseData"
                  :debounce-delay="0"
                  :throttle-interval="0"
                  @update:payload="handleUpdatePayload"
                />
              </div>
            </template>

            <template v-else-if="integrationMode === 'Advoware'">
              <div class="">
                <AdvowareForm
                  v-if="caseStore.caseRecord"
                  ref="advowareFormRef"
                  :case-data="caseStore.caseRecord"
                  :debounce-delay="1000"
                  :throttle-interval="1000"
                  :is-opponent-exported="isOpponentExported"
                  :selected-entities-relationships="selectedEntitiesRelationships"
                  :mapped-entity-types="mappedSelectedEntities"
                  @update:payload="handleUpdatePayload"
                />
              </div>
            </template>

            <template v-if="integrationMode === 'Kleos'">
              <div>
                <KleosForm
                  v-if="caseData"
                  ref="kleosFormRef"
                  :case-data="caseData"
                  :debounce-delay="0"
                  :throttle-interval="0"
                  :is-opponent-exported="false"
                  :selected-entities-relationships="selectedEntitiesRelationships"
                  :mapped-entity-types="mappedSelectedEntities"
                  @update:payload="handleUpdatePayload"
                />
              </div>
            </template>

            <template v-else-if="integrationMode === 'Jurnodes'">
              <div class="">
                <JurnodesForm
                  v-if="caseData"
                  ref="jurnodesFormRef"
                  :case-data="caseData"
                  :debounce-delay="1000"
                  :throttle-interval="1000"
                  :isOpponentExported="isOpponentExported"
                  @update:payload="handleUpdatePayload"
                />
              </div>
            </template>

            <Entities
              v-if="integrationMode !== 'Annotext'"
              :unified-export-mode="true"
              :opponent-enabled="opponentEnabledList.includes(integrationMode as string)"
              :misc-entities-enabled="miscEntitiesEnabledList.includes(integrationMode as string)"
              :insurance-info-enabled="
                integrationMode === 'Actaport' ||
                integrationMode === 'RaMicro' ||
                integrationMode === 'RaMicroApi'
              "
              @update:selected-entities-relationships="handleSelectEntitiesWithRelationships"
            />

            <!-- Personal Information -->
            <div>
              <PersonalInformation
                v-if="caseData && integrationMode === 'Annotext'"
                ref="personalInformationRef"
                :case-data="caseData"
                :debounce-delay="5000"
                :throttle-interval="5000"
                @update:case-data="handleSaveInfo"
              />
            </div>

            <template v-if="integrationMode === 'Annotext'">
              <div>
                <OpponentInformation
                  v-if="caseData"
                  ref="opponentInformationRef"
                  :case-data="caseData"
                  :debounce-delay="5000"
                  :throttle-interval="5000"
                  @update:export-opponent-data="handleExportOpponentData"
                  @update:case-data="handleSaveInfo"
                />
              </div>
            </template>

            <template v-if="integrationMode !== 'RaMicro'">
              <SelectDocumentSection
                v-if="caseData"
                :case-data="caseData"
                :integration-mode="integrationMode"
                :debounce-delay="1000"
                :throttle-interval="1000"
                @update:file-data="handleFileDataUpdate"
                @size-validation="(isValid) => (documentSizeValid = isValid)"
              />
            </template>

            <!-- Submit Button -->
            <div class="flex justify-end pt-2">
              <button
                type="submit"
                :class="[
                  `text-md rounded-lg px-6 py-2 font-semibold shadow-md transition duration-150 ease-in-out
                  focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`,
                  canSubmitForm
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'cursor-not-allowed bg-gray-300 text-gray-500'
                ]"
                :disabled="!canSubmitForm"
                @click="handleSubmit"
              >
                {{ performActionText }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>
    <LoadingComponent
      :show="showModal"
      :status="modalStatus"
      :integration-name="integrationMode || ''"
      @complete="handleModalComplete"
      @reset-status="handleResetStatus"
      @retry="handleRetry"
    />
  </div>
</template>

<style scoped>
  .legacy-bg-color {
    background-color: #f8fafc !important;
  }

  .loading-spinner {
    position: relative;
    width: 80px;
    height: 80px;
    animation: spin 1.5s linear infinite;
  }

  .spinner-quad {
    position: absolute;
    width: 40%;
    height: 40%;
    border-radius: 50%;
    animation: pulse 1.5s ease-in-out infinite;
  }

  .spinner-quad:nth-child(1) {
    top: 0;
    left: 0;
    background-color: #9333ea;
  }

  .spinner-quad:nth-child(2) {
    top: 0;
    right: 0;
    background-color: #a855f7;
    animation-delay: 0.4s;
  }

  .spinner-quad:nth-child(3) {
    bottom: 0;
    right: 0;
    background-color: #c084fc;
    animation-delay: 0.8s;
  }

  .spinner-quad:nth-child(4) {
    bottom: 0;
    left: 0;
    background-color: #e9d5ff;
    animation-delay: 1.2s;
  }

  @keyframes spin {
    100% {
      transform: rotate(360deg);
    }
  }

  @keyframes pulse {
    0%,
    100% {
      transform: scale(0.5);
      opacity: 0.5;
    }
    50% {
      transform: scale(1);
      opacity: 1;
    }
  }
</style>
